// Set the template tags per feature

import { UserPreferenceType } from 'services/settings/SettingsTypes';
import {
    getAppointmentAssignees,
    getLastDoneAppointment,
    getNextAppointment,
    getJobClientPerson,
    getScheduledAppointments,
    mapTagsToNestedData,
    getJobLocationPerson,
    getContactPerson,
    getContactLocation
} from './templateTagResolvers';

export type EmailConnectedFeature =
    | 'jobs'
    | 'appointments'
    | 'proposals'
    | 'orders'
    | 'invoices'
    | 'tickets'
    | 'contacts';

export type TemplateTagType =
    | 'text'
    | 'date'
    | 'time'
    | 'datetime'
    | 'price'
    | 'duration'
    | 'multiline';

export interface TemplateTagContext {
    currentUser?: any;
    translate?: (key: string, fallback: string) => string;
}

export interface TemplateTag {
    key: string;
    nlKey?: string;
    aliasKeys?: string[];
    label: string;
    type: TemplateTagType;
    getValue: (objectData: any, context?: TemplateTagContext) => unknown;
}

// Translate a workflow status name to the readable status label
const translateStatusName = (statusName: unknown, context?: TemplateTagContext): unknown => {
    if (statusName && context?.translate) {
        return context.translate(`workflow.default_status_names.${statusName}`, String(statusName));
    }
    return statusName;
};

const getInvoiceOrderData = (objectData: any) => {
    return objectData?.order ?? {};
};

const getInvoiceJobData = (objectData: any) => {
    return objectData?.job ?? {};
};

const getOrderProposalData = (objectData: any) => {
    return objectData?.proposal ?? {};
};

// Set the job template tags
export const jobTags: TemplateTag[] = [
    {
        key: 'job_number',
        nlKey: 'werkbon_nummer',
        label: 'Werkbonnummer',
        type: 'text',
        getValue: objectData => objectData?.number
    },
    {
        key: 'job_type',
        nlKey: 'werkbon_type',
        label: 'Type',
        type: 'text',
        getValue: objectData => objectData?.job_type_name ?? objectData?.job_type
    },
    {
        key: 'job_internal_reference',
        nlKey: 'werkbon_ons_kenmerk',
        label: 'Ons kenmerk',
        type: 'text',
        getValue: objectData => objectData?.internal_reference
    },
    {
        key: 'job_external_reference',
        nlKey: 'werkbon_klantkenmerk',
        label: 'Klantkenmerk',
        type: 'text',
        getValue: objectData => objectData?.external_reference
    },
    {
        key: 'job_main_description',
        nlKey: 'werkbon_omschrijving',
        label: 'Omschrijving',
        type: 'text',
        getValue: objectData => objectData?.main_description
    },
    {
        key: 'job_target_date',
        nlKey: 'werkbon_richtdatum',
        aliasKeys: ['werkbon_streefdatum'],
        label: 'Richtdatum',
        type: 'date',
        getValue: objectData => objectData?.target_date
    },
    {
        key: 'job_status',
        nlKey: 'werkbon_status',
        label: 'Status',
        type: 'text',
        getValue: (objectData, context) => translateStatusName(objectData?.status_name, context)
    },
    {
        key: 'job_client_name',
        nlKey: 'werkbon_klantnaam',
        label: 'Klantnaam',
        type: 'text',
        getValue: objectData => objectData?.client_name ?? objectData?.client?.name
    },
    {
        key: 'job_client_person_title',
        nlKey: 'werkbon_contactpersoon_aanhef',
        label: 'Aanhef contactpersoon',
        type: 'text',
        getValue: objectData => getJobClientPerson(objectData)?.title
    },
    {
        key: 'job_client_person_first_name',
        nlKey: 'werkbon_contactpersoon_voornaam',
        label: 'Voornaam contactpersoon',
        type: 'text',
        getValue: objectData => getJobClientPerson(objectData)?.first_name
    },
    {
        key: 'job_client_person_last_name',
        nlKey: 'werkbon_contactpersoon_achternaam',
        label: 'Achternaam contactpersoon',
        type: 'text',
        getValue: objectData => getJobClientPerson(objectData)?.last_name
    },
    {
        key: 'job_location_person_title',
        nlKey: 'werkbon_locatie_contactpersoon_aanhef',
        label: 'Aanhef locatiecontactpersoon',
        type: 'text',
        getValue: objectData => getJobLocationPerson(objectData)?.title
    },
    {
        key: 'job_location_person_first_name',
        nlKey: 'werkbon_locatie_contactpersoon_voornaam',
        label: 'Voornaam locatiecontactpersoon',
        type: 'text',
        getValue: objectData => getJobLocationPerson(objectData)?.first_name
    },
    {
        key: 'job_location_person_last_name',
        nlKey: 'werkbon_locatie_contactpersoon_achternaam',
        label: 'Achternaam locatiecontactpersoon',
        type: 'text',
        getValue: objectData => getJobLocationPerson(objectData)?.last_name
    },
    {
        key: 'job_location_name',
        nlKey: 'werkbon_locatie_naam',
        label: 'Locatie naam',
        type: 'text',
        getValue: objectData => objectData?.work_location?.name
    },
    {
        key: 'job_location_street',
        nlKey: 'werkbon_locatie_straat',
        label: 'Locatie straat',
        type: 'text',
        getValue: objectData => objectData?.work_location?.street
    },
    {
        key: 'job_location_house_number',
        nlKey: 'werkbon_locatie_huisnummer',
        label: 'Locatie huisnummer',
        type: 'text',
        getValue: objectData => objectData?.work_location?.house_number
    },
    {
        key: 'job_location_postal_code',
        nlKey: 'werkbon_locatie_postcode',
        label: 'Locatie postcode',
        type: 'text',
        getValue: objectData => objectData?.work_location?.postal_code
    },
    {
        key: 'job_location_city',
        nlKey: 'werkbon_locatie_plaats',
        label: 'Locatie plaats',
        type: 'text',
        getValue: objectData => objectData?.work_location?.city
    },
    {
        key: 'job_location_full_address',
        nlKey: 'werkbon_locatie_volledig_adres',
        label: 'Locatie volledig adres',
        type: 'text',
        getValue: objectData => objectData?.work_location?.address
    },
    {
        key: 'job_next_appointment_date',
        nlKey: 'eerstvolgende_afspraak_datum',
        label: 'Datum eerstvolgende afspraak',
        type: 'date',
        getValue: objectData => getNextAppointment(objectData)?.start_datetime
    },
    {
        key: 'job_next_appointment_start_time',
        nlKey: 'eerstvolgende_afspraak_starttijd',
        label: 'Starttijd eerstvolgende afspraak',
        type: 'time',
        getValue: objectData => getNextAppointment(objectData)?.start_datetime
    },
    {
        key: 'job_next_appointment_end_time',
        nlKey: 'eerstvolgende_afspraak_eindtijd',
        label: 'Eindtijd eerstvolgende afspraak',
        type: 'time',
        getValue: objectData => getNextAppointment(objectData)?.end_datetime
    },
    {
        key: 'job_next_appointment_datetime',
        nlKey: 'eerstvolgende_afspraak_datum_tijd',
        label: 'Datum en tijd eerstvolgende afspraak',
        type: 'datetime',
        getValue: objectData => getNextAppointment(objectData)?.start_datetime
    },
    {
        key: 'job_next_appointment_assignees',
        nlKey: 'eerstvolgende_afspraak_medewerker',
        label: 'Medewerker(s) eerstvolgende afspraak',
        type: 'text',
        getValue: objectData => getAppointmentAssignees(getNextAppointment(objectData))
    },
    {
        key: 'job_last_done_appointment_date',
        nlKey: 'laatste_afgeronde_afspraak_datum',
        label: 'Datum laatste afgeronde afspraak',
        type: 'date',
        getValue: objectData => getLastDoneAppointment(objectData)?.start_datetime
    },
    {
        key: 'job_last_done_appointment_start_time',
        nlKey: 'laatste_afgeronde_afspraak_starttijd',
        label: 'Starttijd laatste afgeronde afspraak',
        type: 'time',
        getValue: objectData => getLastDoneAppointment(objectData)?.start_datetime
    },
    {
        key: 'job_last_done_appointment_assignees',
        nlKey: 'laatste_afgeronde_afspraak_medewerker',
        label: 'Medewerkers laatste afgeronde afspraak',
        type: 'text',
        getValue: objectData => getAppointmentAssignees(getLastDoneAppointment(objectData))
    },
    {
        key: 'job_all_scheduled_appointments',
        nlKey: 'alle_geplande_afspraken',
        label: 'Alle geplande afspraken',
        type: 'multiline',
        getValue: objectData => getScheduledAppointments(objectData)
    }
];

// Set the proposal template tags
export const proposalTags: TemplateTag[] = [
    {
        key: 'proposal_number',
        nlKey: 'offerte_nummer',
        label: 'Offertenummer',
        type: 'text',
        getValue: objectData => objectData?.number
    },
    {
        key: 'proposal_date',
        nlKey: 'offerte_datum',
        label: 'Offertedatum',
        type: 'date',
        getValue: objectData => objectData?.proposal_date
    },
    {
        key: 'proposal_expiration_date',
        nlKey: 'offerte_vervaldatum',
        label: 'Vervaldatum',
        type: 'date',
        getValue: objectData => objectData?.expiration_date
    },
    {
        key: 'proposal_accept_date',
        nlKey: 'offerte_acceptatiedatum',
        label: 'Acceptatiedatum',
        type: 'date',
        getValue: objectData => objectData?.accept_date
    },
    {
        key: 'proposal_amount',
        nlKey: 'offerte_bedrag',
        label: 'Bedrag',
        type: 'price',
        getValue: objectData => objectData?.amount
    },
    {
        key: 'proposal_amount_excl_vat',
        nlKey: 'offerte_bedrag_excl_btw',
        label: 'Bedrag excl. btw',
        type: 'price',
        getValue: objectData => objectData?.amount_excl_vat
    },
    {
        key: 'proposal_internal_reference',
        nlKey: 'offerte_ons_kenmerk',
        label: 'Ons kenmerk',
        type: 'text',
        getValue: objectData => objectData?.internal_reference
    },
    {
        key: 'proposal_external_reference',
        nlKey: 'offerte_klantkenmerk',
        label: 'Klantkenmerk',
        type: 'text',
        getValue: objectData => objectData?.external_reference
    },
    {
        key: 'proposal_status',
        nlKey: 'offerte_status',
        label: 'Status',
        type: 'text',
        getValue: (objectData, context) => translateStatusName(objectData?.status, context)
    },
    {
        key: 'proposal_recipient_name',
        nlKey: 'offerte_ontvanger_naam',
        label: 'Ontvanger naam',
        type: 'text',
        getValue: objectData => objectData?.recipient?.name
    },
    {
        key: 'proposal_recipient_person_title',
        nlKey: 'offerte_contactpersoon_aanhef',
        label: 'Aanhef contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.recipient_person?.title
    },
    {
        key: 'proposal_recipient_person_first_name',
        nlKey: 'offerte_contactpersoon_voornaam',
        label: 'Voornaam contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.recipient_person?.first_name
    },
    {
        key: 'proposal_recipient_person_last_name',
        nlKey: 'offerte_contactpersoon_achternaam',
        label: 'Achternaam contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.recipient_person?.last_name
    },
    {
        key: 'proposal_address_name',
        nlKey: 'offerte_adres_naam',
        label: 'Adres naam',
        type: 'text',
        getValue: objectData => objectData?.address?.name
    },
    {
        key: 'proposal_address_street',
        nlKey: 'offerte_adres_straat',
        label: 'Adres straat',
        type: 'text',
        getValue: objectData => objectData?.address?.street
    },
    {
        key: 'proposal_address_house_number',
        nlKey: 'offerte_adres_huisnummer',
        label: 'Adres huisnummer',
        type: 'text',
        getValue: objectData => objectData?.address?.house_number
    },
    {
        key: 'proposal_address_postal_code',
        nlKey: 'offerte_adres_postcode',
        label: 'Adres postcode',
        type: 'text',
        getValue: objectData => objectData?.address?.postal_code
    },
    {
        key: 'proposal_address_city',
        nlKey: 'offerte_adres_plaats',
        label: 'Adres plaats',
        type: 'text',
        getValue: objectData => objectData?.address?.city
    },
    {
        key: 'proposal_address_full_address',
        nlKey: 'offerte_volledig_adres',
        label: 'Volledig adres',
        type: 'text',
        getValue: objectData => objectData?.address?.address
    }
];

// Set the order template tags
export const orderTags: TemplateTag[] = [
    {
        key: 'order_number',
        nlKey: 'order_nummer',
        label: 'Ordernummer',
        type: 'text',
        getValue: objectData => objectData?.number
    },
    {
        key: 'order_date',
        nlKey: 'order_datum',
        label: 'Orderdatum',
        type: 'date',
        getValue: objectData => objectData?.order_date
    },
    {
        key: 'order_amount',
        nlKey: 'order_bedrag',
        label: 'Bedrag',
        type: 'price',
        getValue: objectData => objectData?.amount
    },
    {
        key: 'order_amount_excl_vat',
        nlKey: 'order_bedrag_excl_btw',
        label: 'Bedrag excl. btw',
        type: 'price',
        getValue: objectData => objectData?.amount_excl_vat
    },
    {
        key: 'order_internal_reference',
        nlKey: 'order_ons_kenmerk',
        label: 'Ons kenmerk',
        type: 'text',
        getValue: objectData => objectData?.internal_reference
    },
    {
        key: 'order_external_reference',
        nlKey: 'order_klantkenmerk',
        label: 'Klantkenmerk',
        type: 'text',
        getValue: objectData => objectData?.external_reference
    },
    {
        key: 'order_client_name',
        nlKey: 'order_klantnaam',
        label: 'Klantnaam',
        type: 'text',
        getValue: objectData => objectData?.client?.name
    },
    {
        key: 'order_client_person_title',
        nlKey: 'order_contactpersoon_aanhef',
        label: 'Aanhef contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.client_person?.title
    },
    {
        key: 'order_client_person_first_name',
        nlKey: 'order_contactpersoon_voornaam',
        label: 'Voornaam contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.client_person?.first_name
    },
    {
        key: 'order_client_person_last_name',
        nlKey: 'order_contactpersoon_achternaam',
        label: 'Achternaam contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.client_person?.last_name
    },
    {
        key: 'order_address_name',
        nlKey: 'order_adres_naam',
        label: 'Adres naam',
        type: 'text',
        getValue: objectData => objectData?.address?.name
    },
    {
        key: 'order_address_street',
        nlKey: 'order_adres_straat',
        label: 'Adres straat',
        type: 'text',
        getValue: objectData => objectData?.address?.street
    },
    {
        key: 'order_address_house_number',
        nlKey: 'order_adres_huisnummer',
        label: 'Adres huisnummer',
        type: 'text',
        getValue: objectData => objectData?.address?.house_number
    },
    {
        key: 'order_address_postal_code',
        nlKey: 'order_adres_postcode',
        label: 'Adres postcode',
        type: 'text',
        getValue: objectData => objectData?.address?.postal_code
    },
    {
        key: 'order_address_city',
        nlKey: 'order_adres_plaats',
        label: 'Adres plaats',
        type: 'text',
        getValue: objectData => objectData?.address?.city
    },
    {
        key: 'order_address_full_address',
        nlKey: 'order_volledig_adres',
        label: 'Volledig adres',
        type: 'text',
        getValue: objectData => objectData?.address?.address
    }
];

// Set the invoice template tags
export const invoiceTags: TemplateTag[] = [
    {
        key: 'invoice_number',
        nlKey: 'factuur_nummer',
        label: 'Factuurnummer',
        type: 'text',
        getValue: objectData => objectData?.number
    },
    {
        key: 'invoice_date',
        nlKey: 'factuur_datum',
        label: 'Factuurdatum',
        type: 'date',
        getValue: objectData => objectData?.invoice_date
    },
    {
        key: 'invoice_due_date',
        nlKey: 'factuur_vervaldatum',
        label: 'Vervaldatum',
        type: 'date',
        getValue: objectData => objectData?.due_date
    },
    {
        key: 'invoice_amount',
        nlKey: 'factuur_bedrag',
        label: 'Bedrag',
        type: 'price',
        getValue: objectData => objectData?.amount
    },
    {
        key: 'invoice_amount_due',
        nlKey: 'factuur_openstaand_bedrag',
        label: 'Openstaand bedrag',
        type: 'price',
        getValue: objectData => objectData?.amount_due
    },
    {
        key: 'invoice_amount_paid',
        nlKey: 'factuur_betaald_bedrag',
        label: 'Betaald bedrag',
        type: 'price',
        getValue: objectData => objectData?.amount_paid
    },
    {
        key: 'invoice_internal_reference',
        nlKey: 'factuur_ons_kenmerk',
        label: 'Ons kenmerk',
        type: 'text',
        getValue: objectData => objectData?.internal_reference
    },
    {
        key: 'invoice_external_reference',
        nlKey: 'factuur_klantkenmerk',
        label: 'Klantkenmerk',
        type: 'text',
        getValue: objectData => objectData?.external_reference
    },
    {
        key: 'invoice_status',
        nlKey: 'factuur_status',
        label: 'Status',
        type: 'text',
        getValue: (objectData, context) => translateStatusName(objectData?.status, context)
    },
    {
        key: 'invoice_payment_link',
        nlKey: 'factuur_betaallink',
        label: 'Betaallink',
        type: 'text',
        getValue: objectData => objectData?.payment_link
    },
    {
        key: 'invoice_recipient_name',
        nlKey: 'factuur_ontvanger_naam',
        label: 'Ontvanger naam',
        type: 'text',
        getValue: objectData => objectData?.recipient?.name
    },
    {
        key: 'invoice_recipient_person_title',
        nlKey: 'factuur_contactpersoon_aanhef',
        label: 'Aanhef contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.recipient_person?.title
    },
    {
        key: 'invoice_recipient_person_first_name',
        nlKey: 'factuur_contactpersoon_voornaam',
        label: 'Voornaam contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.recipient_person?.first_name
    },
    {
        key: 'invoice_recipient_person_last_name',
        nlKey: 'factuur_contactpersoon_achternaam',
        label: 'Achternaam contactpersoon',
        type: 'text',
        getValue: objectData => objectData?.recipient_person?.last_name
    },
    {
        key: 'invoice_address_name',
        nlKey: 'factuur_adres_naam',
        label: 'Factuuradres naam',
        type: 'text',
        getValue: objectData => objectData?.invoice_address?.name
    },
    {
        key: 'invoice_address_street',
        nlKey: 'factuur_adres_straat',
        label: 'Factuuradres straat',
        type: 'text',
        getValue: objectData => objectData?.invoice_address?.street
    },
    {
        key: 'invoice_address_house_number',
        nlKey: 'factuur_adres_huisnummer',
        label: 'Factuuradres huisnummer',
        type: 'text',
        getValue: objectData => objectData?.invoice_address?.house_number
    },
    {
        key: 'invoice_address_postal_code',
        nlKey: 'factuur_adres_postcode',
        label: 'Factuuradres postcode',
        type: 'text',
        getValue: objectData => objectData?.invoice_address?.postal_code
    },
    {
        key: 'invoice_address_city',
        nlKey: 'factuur_adres_plaats',
        label: 'Factuuradres plaats',
        type: 'text',
        getValue: objectData => objectData?.invoice_address?.city
    },
    {
        key: 'invoice_address_full_address',
        nlKey: 'factuur_volledig_adres',
        label: 'Volledig factuuradres',
        type: 'text',
        getValue: objectData => objectData?.invoice_address?.address
    }
];

// Set the contact template tags, used on the contact, organization, person and location pages
export const contactTags: TemplateTag[] = [
    {
        key: 'contact_name',
        nlKey: 'contact_naam',
        label: 'Naam',
        type: 'text',
        getValue: objectData => objectData?.name
    },
    {
        key: 'contact_number',
        nlKey: 'contact_nummer',
        label: 'Contactnummer',
        type: 'text',
        getValue: objectData => objectData?.number
    },
    {
        key: 'contact_reference',
        nlKey: 'contact_kenmerk',
        label: 'Kenmerk',
        type: 'text',
        getValue: objectData => objectData?.reference
    },
    {
        key: 'contact_person_title',
        nlKey: 'contact_persoon_aanhef',
        label: 'Aanhef contactpersoon',
        type: 'text',
        getValue: objectData => getContactPerson(objectData)?.title
    },
    {
        key: 'contact_person_first_name',
        nlKey: 'contact_persoon_voornaam',
        label: 'Voornaam contactpersoon',
        type: 'text',
        getValue: objectData => getContactPerson(objectData)?.first_name
    },
    {
        key: 'contact_person_last_name',
        nlKey: 'contact_persoon_achternaam',
        label: 'Achternaam contactpersoon',
        type: 'text',
        getValue: objectData => getContactPerson(objectData)?.last_name
    },
    {
        key: 'contact_location_name',
        nlKey: 'contact_locatie_naam',
        label: 'Locatie naam',
        type: 'text',
        getValue: objectData => getContactLocation(objectData)?.name
    },
    {
        key: 'contact_location_street',
        nlKey: 'contact_locatie_straat',
        label: 'Locatie straat',
        type: 'text',
        getValue: objectData => getContactLocation(objectData)?.street
    },
    {
        key: 'contact_location_house_number',
        nlKey: 'contact_locatie_huisnummer',
        label: 'Locatie huisnummer',
        type: 'text',
        getValue: objectData => getContactLocation(objectData)?.house_number
    },
    {
        key: 'contact_location_postal_code',
        nlKey: 'contact_locatie_postcode',
        label: 'Locatie postcode',
        type: 'text',
        getValue: objectData => getContactLocation(objectData)?.postal_code
    },
    {
        key: 'contact_location_city',
        nlKey: 'contact_locatie_plaats',
        label: 'Locatie plaats',
        type: 'text',
        getValue: objectData => getContactLocation(objectData)?.city
    },
    {
        key: 'contact_location_full_address',
        nlKey: 'contact_locatie_volledig_adres',
        label: 'Locatie volledig adres',
        type: 'text',
        getValue: objectData => getContactLocation(objectData)?.address
    }
];

// Set the user template tags
export const userTags: TemplateTag[] = [
    {
        key: 'sender_name',
        nlKey: 'naam_verzender',
        label: 'Naam verzender',
        type: 'text',
        getValue: (_objectData, context) => {
            const firstName = context?.currentUser?.first_name ?? ''
            const lastName = context?.currentUser?.last_name ?? ''
            return [firstName, lastName].filter(Boolean).join(' ')
        }
    },
    {
        key: 'sender_first_name',
        nlKey: 'voornaam_verzender',
        label: 'Voornaam verzender',
        type: 'text',
        getValue: (_objectData, context) => context?.currentUser?.first_name
    },
    {
        key: 'sender_last_name',
        nlKey: 'achternaam_verzender',
        label: 'Achternaam verzender',
        type: 'text',
        getValue: (_objectData, context) => context?.currentUser?.last_name
    }
]

// Set the service desk request template tags, the backend resolves them during sending
export const ticketTags: TemplateTag[] = [
    {
        key: 'ticket_number',
        nlKey: 'aanvraag_nummer',
        label: 'Aanvraagnummer',
        type: 'text',
        getValue: objectData => objectData?.number
    },
    {
        key: 'ticket_subject',
        nlKey: 'aanvraag_onderwerp',
        label: 'Onderwerp',
        type: 'text',
        getValue: objectData => objectData?.subject
    },
    {
        key: 'ticket_description',
        nlKey: 'aanvraag_omschrijving',
        label: 'Omschrijving aanvraag',
        type: 'multiline',
        getValue: objectData => objectData?.description
    },
    {
        key: 'reply_text',
        nlKey: 'reactie_tekst',
        label: 'Reactietekst',
        type: 'multiline',
        getValue: objectData => objectData?.reply
    },
    {
        key: 'requester_name',
        nlKey: 'aanvrager_naam',
        label: 'Naam aanvrager',
        type: 'text',
        getValue: objectData => objectData?.reporter_display_name
    },
    {
        key: 'requester_first_name',
        nlKey: 'aanvrager_voornaam',
        label: 'Voornaam aanvrager',
        type: 'text',
        getValue: objectData => objectData?.reporter_first_name
    },
    {
        key: 'requester_email',
        nlKey: 'aanvrager_email',
        label: 'E-mailadres aanvrager',
        type: 'text',
        getValue: objectData => objectData?.reporter_email
    },
];

export const templateTagsByFeature: Record<EmailConnectedFeature, TemplateTag[]> = {
    jobs: [
        ...jobTags,
        ...userTags
    ],
    appointments: [
        ...jobTags,
        ...userTags
    ],
    proposals: [
        ...proposalTags,
        ...userTags
    ],
    orders: [
        ...orderTags,
        ...mapTagsToNestedData(proposalTags, getOrderProposalData),
        ...userTags
    ],
    invoices: [
        ...invoiceTags,
        ...mapTagsToNestedData(orderTags, getInvoiceOrderData),
        ...mapTagsToNestedData(jobTags, getInvoiceJobData),
        ...userTags
    ],
    tickets: [
        ...ticketTags,
        ...userTags
    ],
    contacts: [
        ...contactTags,
        ...userTags
    ]
};